import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { numKm } from './formato';
import { loadLeafletCDN, rutaOptima, ROUTE_BLUE, OPT_ORANGE } from './rutas';

// Mapa del viaje a pantalla completa: lo abre el QR / enlace del ticket del pasajero.
// Solo el mapa con la ruta real y la optima; para salir, el "atras" del telefono o del navegador.
// Sin 'inset' de CSS: el WebView de Android viejo (83) no lo entiende y el mapa quedaba de tamano cero (negro).
const API_BASE = 'https://cyker.satelitax.com.ar/api';
const INK = '#1F2328';

const dot = (L, color) => L.divIcon({
  html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.45)"></div>`,
  className: '', iconSize: [14, 14], iconAnchor: [7, 7],
});

function Recorrido() {
  const { qrToken } = useParams();
  const mapRef = useRef(null);
  const [estado, setEstado] = useState('cargando'); // cargando | listo | sin-datos | error
  const [km, setKm] = useState({ real: null, optima: null });

  useEffect(() => {
    let mapa = null;
    let vivo = true;
    let observador = null;
    let alCambiarTamano = null;
    (async () => {
      try {
        const [viaje, pos] = await Promise.all([
          fetch(`${API_BASE}/viajes/qr/${encodeURIComponent(qrToken)}`, { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('viaje')))),
          fetch(`${API_BASE}/viajes/qr/${encodeURIComponent(qrToken)}/posiciones`).then((r) => r.json()),
        ]);
        const positions = Array.isArray(pos) ? pos : [];
        if (positions.length < 2) { if (vivo) setEstado('sin-datos'); return; }
        await new Promise((ok) => loadLeafletCDN(ok));
        if (!vivo || !mapRef.current) return;
        const L = window.L;
        mapa = L.map(mapRef.current, { zoomControl: false, attributionControl: true });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap', maxZoom: 19,
        }).addTo(mapa);
        const real = L.polyline(positions, { color: ROUTE_BLUE, weight: 5, opacity: 0.95 }).addTo(mapa);
        L.marker(positions[0], { icon: dot(L, '#22C55E') }).addTo(mapa);
        L.marker(positions[positions.length - 1], { icon: dot(L, '#EF4444') }).addTo(mapa);
        const encuadre = { paddingTopLeft: [28, 28], paddingBottomRight: [28, 96] };
        let limites = real.getBounds();
        mapa.fitBounds(limites, encuadre);
        // Dentro de la app (WebViewActivity) la pagina puede arrancar con el contenedor sin su tamano final:
        // Leaflet calculaba el zoom y el area de dibujo con ese tamano chico y despues las lineas salian
        // cortadas en un rectangulo central y el mapa muy alejado (Adolfo 29-oct, viaje 2555). Cada vez que
        // cambia el tamano se le avisa a Leaflet y se vuelve a encuadrar el recorrido.
        alCambiarTamano = () => { if (mapa) { mapa.invalidateSize(); mapa.fitBounds(limites, encuadre); } };
        if (window.ResizeObserver) {
          observador = new window.ResizeObserver(() => alCambiarTamano());
          observador.observe(mapRef.current);
        }
        window.addEventListener('resize', alCambiarTamano);
        setTimeout(alCambiarTamano, 300);
        const realM = parseFloat(viaje.distancia_metros) || 0;
        setKm({ real: realM > 0 ? numKm(realM) : null, optima: null });
        setEstado('listo');

        const opt = await rutaOptima(positions, realM);
        if (!vivo || !opt) return;
        opt.tramos.forEach((t) => {
          const linea = L.polyline(t, { color: OPT_ORANGE, weight: 4, opacity: 0.9, dashArray: '10 8' }).addTo(mapa);
          limites = limites.extend(linea.getBounds());
        });
        real.bringToFront();
        mapa.fitBounds(limites, encuadre);
        setKm((k) => ({ ...k, optima: numKm(opt.dist) }));
      } catch (_) {
        if (vivo) setEstado('error');
      }
    })();
    return () => {
      vivo = false;
      if (observador) observador.disconnect();
      if (alCambiarTamano) window.removeEventListener('resize', alCambiarTamano);
      if (mapa) mapa.remove();
    };
  }, [qrToken]);

  const mensaje = estado === 'cargando' ? 'Cargando el recorrido…'
    : estado === 'sin-datos' ? 'Este viaje no tiene recorrido registrado.'
    : estado === 'error' ? 'No se encontró el viaje. Revisá el enlace del ticket.' : null;

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: '#E9EEF1', fontFamily: 'Manrope, "Segoe UI", Arial, sans-serif' }}>
      <div ref={mapRef} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }} aria-label="Mapa del recorrido del viaje" />
      {mensaje && (
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center', color: INK, fontSize: '16px' }}>
          {mensaje}
        </div>
      )}
      {estado === 'listo' && (
        <div style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', zIndex: 1000,
          display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
          background: 'rgba(255,255,255,.96)', borderRadius: '999px', padding: '10px 18px',
          boxShadow: '0 6px 22px rgba(31,35,40,.18)', color: INK, fontSize: '13.5px', fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <i style={{ width: '18px', height: '4px', borderRadius: '2px', background: ROUTE_BLUE, display: 'inline-block', marginRight: '7px' }} />
            Real{km.real ? ` ${km.real} km` : ''}
          </span>
          {km.optima && (
            <span style={{ display: 'flex', alignItems: 'center', marginLeft: '18px' }}>
              <i style={{ width: '18px', height: '0', borderTop: `3px dashed ${OPT_ORANGE}`, display: 'inline-block', marginRight: '7px' }} />
              Óptima {km.optima} km
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default Recorrido;
